<template>
  <div class="question-item" :class="{ required: question.required }">
    <label class="question-label">
      {{ question.label }}
      <span v-if="question.required" class="required-indicator">*</span>
    </label>
    <p v-if="question.hint" class="question-hint">{{ question.hint }}</p>

    <!-- Text Input -->
    <input
      v-if="question.type === 'text'"
      type="text"
      class="form-control"
      :value="answer.answer"
      :required="question.required"
      :disabled="disabled"
      @input="$emit('update', $event.target.value)"
    />

    <!-- Textarea -->
    <textarea
      v-else-if="question.type === 'textarea'"
      class="form-control"
      :value="answer.answer"
      :required="question.required"
      :disabled="disabled"
      rows="3"
      @input="$emit('update', $event.target.value)"
    ></textarea>

    <!-- Radio Buttons -->
    <div v-else-if="question.type === 'radio'" class="options-list">
      <template v-for="(option, idx) in question.options" :key="idx">
        <label
          class="option-label"
          :class="{ 'is-disabled': disabled }"
          @click.prevent="!disabled && handleRadioClick(option)"
        >
          <input
            type="radio"
            :name="`question-${question.id}`"
            :value="getOptionValue(option)"
            :checked="isOptionSelected(option)"
            :required="question.required"
            :disabled="disabled"
          />
          <span>{{ getOptionLabel(option) }}</span>
        </label>

        <!-- LE CHAMP EST SOUS LA CASE QUI L'OUVRE, pas au bas de la liste : ce
             qu'on écrit précise CETTE réponse-là, et l'en éloigner obligerait à
             refaire le lien de tête. -->
        <input
          v-if="estOptionAutre(option) && autreChoisie"
          type="text"
          class="form-control champ-autre"
          :value="answer.answer || ''"
          :placeholder="PLACEHOLDER_AUTRE"
          :aria-label="`${getOptionLabel(option)} — précisez`"
          :maxlength="TEXTE_AUTRE_MAX"
          :disabled="disabled"
          @input="majTexteAutre($event.target.value)"
        />
      </template>
    </div>

    <!-- Checkboxes -->
    <div v-else-if="question.type === 'checkbox'" class="options-list">
      <label
        v-for="(option, idx) in question.options"
        :key="idx"
        class="option-label"
        :class="{ 'is-disabled': disabled }"
      >
        <input
          type="checkbox"
          :value="getOptionValue(option)"
          :checked="isOptionSelected(option)"
          :disabled="disabled"
          @change="handleCheckboxChange(getOptionValue(option), $event.target.checked)"
        />
        <span>{{ getOptionLabel(option) }}</span>
      </label>
    </div>

    <!-- Select Dropdown -->
    <select
      v-else-if="question.type === 'select'"
      class="form-control"
      :value="answer.selectedOptions?.[0] || ''"
      :required="question.required"
      :disabled="disabled"
      @change="choisirDansListe($event.target.value)"
    >
      <option value="">Sélectionner...</option>
      <option
        v-for="(option, idx) in question.options"
        :key="idx"
        :value="getOptionValue(option)"
      >
        {{ getOptionLabel(option) }}
      </option>
    </select>

    <!-- Info (Read-only) -->
    <div v-else-if="question.type === 'info'" class="info-box">
      <i class="fas fa-info-circle"></i>
      <span>{{ question.label }}</span>
    </div>


    <!-- Le texte libre d'une liste déroulante ne peut pas se glisser dans le
         menu : il se pose juste dessous, à la place où le regard descend. POSÉ
         APRÈS LA CHAÎNE DES TYPES et non entre `select` et `info` : un `v-if`
         intercalé aurait fait du bloc « info » son `v-else-if` à lui. -->
    <input
      v-if="question.type === 'select' && autreChoisie"
      type="text"
      class="form-control champ-autre"
      :value="answer.answer || ''"
      :placeholder="PLACEHOLDER_AUTRE"
      :aria-label="`${libelleDeLAutre} — précisez`"
      :maxlength="TEXTE_AUTRE_MAX"
      :disabled="disabled"
      @input="majTexteAutre($event.target.value)"
    />

    <!-- LES DOCUMENTS : une réponse choisie (ou une zone de texte) peut ouvrir
         un dépôt de fichiers, avec la consigne de l'administrateur. Seulement
         là où l'écran sait à quelle demande les rattacher. -->
    <template v-if="contexteDocuments">
      <ReponseDocuments
        v-for="depot in depots"
        :key="depot.reponse ?? '__question'"
        :question="question"
        :reponse="depot.reponse"
        :consigne="depot.consigne"
        :contexte="contexteDocuments"
        :disabled="disabled"
      />
    </template>

    <!-- LES CONSEILS S'ADRESSENT AU COMMERCIAL, jamais au client : ils ne
         s'affichent que dans un écran interne, au moment où la réponse tombe.
         L'API publique ne les envoie d'ailleurs plus. -->
    <QuestionConseils v-if="interne || avecConseils" :regles="question.conseils" :reponses="reponsesRetenues(answer, question)" />

    <!-- LA NOTE DU COMMERCIAL, ET SEULEMENT DANS SES ÉCRANS (`interne`). Ce
         qu'il apprend pendant l'appel — un accès difficile, une nuance sur une
         réponse — finissait dans le commentaire général du dossier, détaché de
         la question qui l'a fait naître. Elle se pose sous la réponse qu'elle
         commente, et le client ne la voit jamais : il remplit le même
         questionnaire de son côté. -->
    <div v-if="montreCommentaire" class="bloc-commentaire">
      <label :for="idCommentaire" class="label-commentaire">
        <i class="fas fa-pen-to-square" aria-hidden="true"></i>
        {{ question.commentaireNote || CONSIGNE_PAR_DEFAUT }}
      </label>
      <textarea
        :id="idCommentaire"
        class="form-control champ-commentaire"
        :value="answer.commentaire || ''"
        rows="2"
        :disabled="disabled"
        :maxlength="COMMENTAIRE_MAX"
        @input="$emit('update-commentaire', $event.target.value)"
      ></textarea>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { reponsesRetenues } from '../../utils/conseilsQuestions.js'
import {
  TEXTE_AUTRE_MAX, VALEUR_AUTRE, autreEstChoisie, estOptionAutre,
  libelleOption, optionAutreDe, valeurOption,
} from '../../../shared/question-options.js'
import QuestionConseils from '../questionnaire-formation/QuestionConseils.vue'
import ReponseDocuments from './ReponseDocuments.vue'
import { depotsOuverts } from '../../../shared/documents-reponses.js'
import { useDocumentsReponses } from '../../composables/questionnaires/useReponseDocuments.js'

const props = defineProps({
  question: {
    type: Object,
    required: true
  },
  answer: {
    type: Object,
    default: () => ({ answer: null, selectedOptions: [] })
  },
  disabled: {
    type: Boolean,
    default: false
  },
  /**
   * L'écran est-il interne à Alertis (tunnel du commercial, analyse du besoin) ?
   * Faux dans le questionnaire d'organisation, que le client remplit lui-même.
   */
  interne: {
    type: Boolean,
    default: false
  },
  /**
   * Conseils affichés sans le reste de l'écran interne (note du commercial,
   * bloc « Type d'établissement ») : pour l'ancien tunnel du commercial.
   */
  avecConseils: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update', 'update-commentaire'])

/** Le dépôt de documents, si l'écran en fournit un (pas dans la demande de devis). */
const contexteDocuments = useDocumentsReponses()
const depots = computed(() => depotsOuverts(props.question, props.answer))

const PLACEHOLDER_AUTRE = 'Précisez votre réponse'

/** Bornes et intitulé du champ de note interne. */
const COMMENTAIRE_MAX = 2000
const CONSIGNE_PAR_DEFAUT = 'Note interne (non visible par le client)'

/**
 * Le champ n'apparaît que si l'écran est INTERNE et la question l'a ouvert. Les
 * deux conditions comptent : le questionnaire d'organisation monte le même
 * composant, et une note « non visible par le client » affichée au client
 * serait exactement le contraire de ce qu'elle promet.
 */
const montreCommentaire = computed(() => (
  props.interne && Boolean(props.question?.commentaireActif)
))


const idCommentaire = computed(() => `commentaire-question-${props.question?.id}`)

// Les deux formes d'une option — chaîne nue ou objet — sont ramenées à une
// seule au même endroit pour tout le dépôt (shared/question-options.js).
const getOptionValue = valeurOption
const getOptionLabel = libelleOption

/** La case « Autre » est-elle cochée ? C'est elle qui ouvre le champ libre. */
const autreChoisie = computed(() => autreEstChoisie(props.question, props.answer))

/** Le libellé de la case, pour nommer le champ aux lecteurs d'écran. */
const libelleDeLAutre = computed(() => libelleOption(optionAutreDe(props.question)) || 'Autre')

// Check if option is selected (handles both selectedOptions array and answer string for backward compatibility)
function isOptionSelected(option) {
  const value = getOptionValue(option)
  const choisies = props.answer.selectedOptions
  // Check in selectedOptions array first
  if (Array.isArray(choisies) && choisies.length) {
    return choisies.includes(value)
  }
  // Repli sur `answer` pour les réponses enregistrées avant `selectedOptions`.
  // IL NE VAUT QUE FAUTE DE CHOIX ENREGISTRÉ : avec « Autre » cochée, `answer`
  // porte le texte libre, et un client qui y recopie le libellé d'une autre
  // réponse verrait deux cases cochées à la fois.
  return props.answer.answer === value
}

// Handle radio click - allows unchecking by clicking on selected radio
function handleRadioClick(option) {
  const value = getOptionValue(option)
  if (isOptionSelected(option)) {
    // Uncheck - emit empty array
    emit('update', [])
  } else if (estOptionAutre(option)) {
    // Choisir « Autre » ouvre le champ SANS effacer ce qui y avait été écrit :
    // on se ravise souvent après avoir essayé une autre réponse.
    emit('update', { selectedOptions: [VALEUR_AUTRE], answer: props.answer.answer || null })
  } else {
    // Une réponse écrite d'avance chasse le texte libre : le garder ferait
    // porter au dossier une précision qui ne se rattache plus à rien.
    emit('update', value)
  }
}

/**
 * Le texte libre ne se saisit qu'avec « Autre » cochée : les deux partent
 * ensemble, sinon l'écran d'accueil de la réponse rangerait le texte seul et
 * perdrait le choix.
 */
function majTexteAutre(texte) {
  emit('update', { selectedOptions: [VALEUR_AUTRE], answer: texte })
}

/** Même règle que le bouton radio, pour la liste déroulante. */
function choisirDansListe(valeur) {
  if (valeur === VALEUR_AUTRE) {
    emit('update', { selectedOptions: [VALEUR_AUTRE], answer: props.answer.answer || null })
    return
  }
  emit('update', valeur)
}

function handleCheckboxChange(optionValue, checked) {
  const currentOptions = [...(props.answer.selectedOptions || [])]
  if (checked) {
    if (!currentOptions.includes(optionValue)) {
      currentOptions.push(optionValue)
    }
  } else {
    const index = currentOptions.indexOf(optionValue)
    if (index > -1) {
      currentOptions.splice(index, 1)
    }
  }
  emit('update', currentOptions)
}
</script>

<style scoped>
.question-item {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.question-label {
  font-weight: 500;
  color: var(--color-text-primary);
  font-size: var(--font-size-sm);
}

.question-hint {
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  margin: 0;
}



.options-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.option-label {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  cursor: pointer;
  font-weight: normal;
  font-size: var(--font-size-sm);
}

.option-label input {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.option-label.is-disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.option-label.is-disabled input {
  cursor: not-allowed;
}

/* Décalé sous sa case, comme un enfant de la réponse qu'il précise. */
.champ-autre {
  margin-left: calc(16px + var(--spacing-sm));
}

/* Détachée de la question par un filet : ce bloc n'est pas une question de
   plus, c'est ce qu'Alertis note à côté de la réponse. */
.bloc-commentaire {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  margin-top: var(--spacing-xs);
  padding-top: var(--spacing-xs);
  border-top: 1px dashed var(--color-border);
}

.label-commentaire {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
}

.champ-commentaire {
  font-size: var(--font-size-sm);
}

.info-box {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--color-info-lightest);
  border: 1px solid var(--color-info);
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  color: var(--color-info);
}

.info-box i {
  margin-top: 2px;
}
</style>
